// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import api from "../services/api";
import { setAuthToken } from "../services/apiConfig";
import { clearAllCartStorage, syncCartScope } from "../utils/cart";
import i18n from "../i18n";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyUserLanguage = (nextUser) => {
    const local = (localStorage.getItem("chafadi-lang") || "")
      .toString()
      .trim()
      .toLowerCase();
    if (local === "en" || local === "fr") {
      i18n.changeLanguage(local);
      localStorage.setItem("chafadi-lang", local);
      const serverLanguage = (nextUser?.language || "")
        .toString()
        .trim()
        .toLowerCase();
      if ((serverLanguage === "fr" ? "fr" : "en") !== local) {
        api.patch("/me/language/", { language: local }).catch(() => {});
      }
      return;
    }

    const raw = (nextUser?.language || "").toString().trim().toLowerCase();
    const preferred = raw === "fr" ? "fr" : "en";
    i18n.changeLanguage(preferred);
    localStorage.setItem("chafadi-lang", preferred);
  };

  useEffect(() => {
    const initializeAuth = async () => {
      console.log("AuthContext: Initializing authentication...");
      const storedToken = localStorage.getItem("chafadi-token");
      if (storedToken) {
        setAuthToken(storedToken);
        try {
          const response = await api.get("me/");
          setUser(response.data);
          applyUserLanguage(response.data);
          console.log(
            "AuthContext: User data fetched from API:",
            response.data
          );
          console.log(
            "AuthContext: is_first_login (from API):",
            response.data.is_first_login
          );
        } catch (error) {
          console.error(
            "AuthContext: Session expired or invalid token. Logging out.",
            error
          );
          localStorage.removeItem("chafadi-token");
          localStorage.removeItem("chafadi-refresh-token"); // Ensure refresh token is also cleared
          setAuthToken(null);
          setUser(null);
        }
      } else {
        console.log("AuthContext: No token found. User is not authenticated.");
      }
      setLoading(false);
      console.log(
        "AuthContext: Initialization complete. Loading is now FALSE."
      );
    };
    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post("auth/login/", { email, password });
      const accessToken = response.data.access;
      const refreshToken = response.data.refresh; // Assuming refresh token is also returned

      localStorage.setItem("chafadi-token", accessToken);
      if (refreshToken) {
        localStorage.setItem("chafadi-refresh-token", refreshToken);
      }

      setAuthToken(accessToken);
      syncCartScope();
      const userResponse = await api.get("me/");
      setUser(userResponse.data);
      applyUserLanguage(userResponse.data);
      console.log(
        "AuthContext: Login successful, user set:",
        userResponse.data
      );
      return userResponse.data;
    } catch (error) {
      console.error("Login Error:", error.response);
      // Our new CustomLoginSerializer will now provide this specific error message
      throw new Error(
        error.response?.data?.detail ||
          "Login failed. Please check credentials."
      );
    }
  };

  // --- FIX: Signup function now expects a FormData object ---
  const signup = async (signupFormData) => {
    try {
      // This function's only job is to hit the registration endpoint.
      // It now accepts and sends a FormData object.
      // Axios will automatically set the correct 'multipart/form-data' header.
      await api.post("/auth/registration/", signupFormData);
      console.log(
        "AuthContext: Signup request successful. User needs to verify email."
      );
      // No explicit return on success; the success is inferred by no error being thrown.
    } catch (error) {
      console.error("❌ Signup Error:", error.response?.data);
      const errorData = error.response?.data;
      if (errorData) {
        // More robust error parsing for various backend responses
        const formattedError = Object.entries(errorData)
          .map(
            ([field, messages]) =>
              `${field.charAt(0).toUpperCase() + field.slice(1)}: ${
                Array.isArray(messages) ? messages.join(" ") : messages
              }`
          )
          .join(" | ");
        throw new Error(formattedError || "An unknown error occurred.");
      }
      throw new Error("An unknown error occurred during signup.");
    }
  };

  const logout = () => {
    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.disableAutoSelect();
      } catch (_) {}
      try {
        if (user?.email) {
          window.google.accounts.id.revoke(user.email, () => {});
        }
      } catch (_) {}
    }
    localStorage.removeItem("chafadi-token");
    localStorage.removeItem("chafadi-refresh-token"); // Clear refresh token as well
    clearAllCartStorage();
    setAuthToken(null);
    setUser(null);
    console.log("AuthContext: User logged out.");
  };

  const completeOnboarding = (updatedUser) => {
    setUser(updatedUser);
    applyUserLanguage(updatedUser);
    console.log(
      "AuthContext: Onboarding complete. User state updated:",
      updatedUser
    );
    console.log(
      "AuthContext: is_first_login (after onboarding):",
      updatedUser.is_first_login
    );
  };

  // NEW FUNCTION
  const loginWithToken = (data) => {
    const { access, refresh, user: userData } = data;
    localStorage.setItem("chafadi-token", access);
    if (refresh) localStorage.setItem("chafadi-refresh-token", refresh);

    setAuthToken(access); // Configure axios
    syncCartScope();
    setUser(userData); // Set the user state directly
    applyUserLanguage(userData);
    console.log("AuthContext: Logged in directly with token. User:", userData);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    signup, // signup is now prepared for FormData
    logout,
    completeOnboarding,
    loginWithToken, // Add loginWithToken to the context value
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
